/**
 * DetachedWindow — a portal into a real OS window.
 *
 * It **opens the window itself** and closes it in the same effect's cleanup.
 * That symmetry is not tidiness; see below.
 *
 * ## The children stay in the opener's React tree
 *
 * `createPortal` moves the DOM and not the tree, so context still flows: the
 * panel's `useTheme`, `useTokenResolver` and the workspace dispatch all work
 * unchanged, and a click on the second monitor mutates state on the first. A
 * second `createRoot` in the popup would render the same components and cut all
 * of it — every provider would have to be repeated, and the two trees would then
 * have to be kept in step by hand.
 *
 * React attaches native listeners to a portal container as well as to the root,
 * which is why a click inside another document is heard at all.
 *
 * ## Open and close belong to the same effect
 *
 * The first version took an already-opened `Window` as a prop and closed it on
 * unmount, on the reasoning that `window.open` must happen inside the click. It
 * flashed a window open and shut, and the cause is the one React warns about:
 * **StrictMode runs every effect mount → cleanup → mount**, so the cleanup
 * closed the window a moment after it appeared. An unstable `onClose` in the
 * deps did the same thing a second way, on every render.
 *
 * Opening here makes the pair symmetric, which is exactly what StrictMode
 * checks: open, close, open again, and what is left is an open window. The
 * gesture worry was overcautious — user activation is *transient* and lasts
 * seconds, not one task, so a layout effect running microseconds after a click
 * is still inside it.
 *
 * ## Neither side outlives the other
 *
 * A popup whose opener reloaded holds a React tree that will never update again:
 * it looks alive, and every button in it is inert. A panel whose window was
 * closed with nobody listening is gone with no way back. Both are handled, and
 * the second is the one a user hits every day.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { useTheme } from "./theme";
import { applyDocumentTheme, openDataWindow, setDocumentTitle } from "../lib/detach";

export interface DetachedWindowProps {
  /** Names the taskbar entry. */
  title: string;
  /**
   * Come home — the window closed, or was never allowed to open.
   *
   * Read through a ref, so its identity can change freely: it is an inline arrow
   * at the call site, and in the effect's deps it would re-run the effect on
   * every render, closing the window each time.
   */
  onClose: () => void;
  /**
   * The browser refused to open a window at all.
   *
   * Separate from {@link onClose} because the two mean opposite things to the
   * control that sent us here: a window that closed should leave the detach
   * offer standing, and a window that was never allowed should not. Both still
   * come home — this fires *as well as* `onClose`, not instead of it.
   *
   * Fires twice under StrictMode, once per effect mount. Recording a refusal is
   * idempotent, so the caller does not have to care.
   */
  onRefused?: (() => void) | undefined;
  /**
   * The window's name: the whole panel's by default, or one view's
   * (`viewWindowName`). Read once, when the window opens.
   */
  name?: string | undefined;
  children: ReactNode;
}

export function DetachedWindow({ title, onClose, onRefused, name, children }: DetachedWindowProps) {
  const theme = useTheme();
  const [win, setWin] = useState<Window | null>(null);
  // The mount point is state rather than a ref, because the first render happens
  // before the document is prepared and portalling into an unprepared body
  // flashes the panel on white.
  const [mount, setMount] = useState<HTMLElement | null>(null);

  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  // Same reasoning as `closeRef`: an inline arrow at the call site, and in the
  // effect's deps it would reopen the window on every render.
  const refusedRef = useRef(onRefused);
  useEffect(() => {
    refusedRef.current = onRefused;
  }, [onRefused]);

  useLayoutEffect(() => {
    const opened = openDataWindow(undefined, name);
    if (!opened) {
      // A blocker denies silently, so this is an outcome rather than an error:
      // the panel comes home and stays docked instead of portalling into
      // nothing. It is reported as well as handled — coming home without a word
      // is indistinguishable from a control that does nothing, which is exactly
      // how this was read.
      refusedRef.current?.();
      closeRef.current();
      return;
    }
    const doc = opened.document;
    // A blank popup normally has a body by the time `open` returns; a browser
    // still parsing `about:blank` would give `null` and the portal would mount
    // into nothing.
    if (doc.body) {
      // A reused window — `window.open` returns the existing one for a name it
      // already knows — still holds the last panel's DOM. React removes its own
      // nodes on unmount, but a reload of the opener leaves them behind.
      doc.body.replaceChildren();
      const host = doc.createElement("div");
      host.style.height = "100%";
      doc.body.appendChild(host);
      setMount(host);
    }
    setWin(opened);

    return () => {
      setMount(null);
      setWin(null);
      // Unmounting *is* the panel coming home — the caller already decided — so
      // the window goes with it. `onClose` is deliberately not called here: it
      // is what got us here.
      opened.close();
    };
  }, []);

  // Re-applied on every theme change, or a panel detached under one theme keeps
  // that palette while the chart behind it repaints.
  useLayoutEffect(() => {
    if (win) applyDocumentTheme(win.document, theme);
  }, [win, theme]);

  useLayoutEffect(() => {
    if (win) setDocumentTitle(win.document, title);
  }, [win, title]);

  useEffect(() => {
    if (!win) return;
    // **The window closing has to come home.** `pagehide` fires on the popup's
    // own unload; `closed` is polled as well because a window shut from the OS
    // chrome does not always run its handlers, and a panel that is gone with no
    // way back is the failure a user meets first.
    const home = () => closeRef.current();
    win.addEventListener("pagehide", home);
    const poll = window.setInterval(() => {
      if (win.closed) home();
    }, 500);

    // **The opener closing has to take the popup with it.** Otherwise a reload
    // strands a window holding a React tree that will never update again — it
    // looks alive and every control in it is inert.
    const shut = () => win.close();
    window.addEventListener("pagehide", shut);

    return () => {
      win.removeEventListener("pagehide", home);
      window.removeEventListener("pagehide", shut);
      window.clearInterval(poll);
    };
  }, [win]);

  return mount ? createPortal(children, mount) : null;
}
